import { useSyncExternalStore } from 'react';
import type { AppConfig } from '@mitang/shared';
import { listenAppConfig } from '../lib/repo';

/**
 * Single shared app_config/global listener via useSyncExternalStore so multiple
 * components (force-update gate, sync footer) share ONE Firestore listener.
 */
let config: AppConfig | null = null;
let started = false;
let refCount = 0;
let unsub: (() => void) | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  unsub = listenAppConfig(
    (c) => {
      config = c;
      emit();
    },
    () => {
      // Keep last-known config on error/offline.
    },
  );
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  refCount += 1;
  ensureStarted();
  return () => {
    listeners.delete(cb);
    refCount -= 1;
    if (refCount === 0 && unsub) {
      unsub();
      unsub = null;
      started = false;
    }
  };
}

function getSnapshot(): AppConfig | null {
  return config;
}

export function useAppConfig(): AppConfig | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
